import { IAuthenticationClientRedirecter } from '@autopylot-internal/autopylot-api-client';
import { Device } from '../support/Device.ts';
import { Browser } from '@capacitor/browser';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Logger } from '../support/debug/Logger.ts';
import { EnvConfiguration } from '../app/config/EnvConfiguration.ts';
import { PluginListenerHandle } from '@capacitor/core';

export class BrowserAuthenticationClientRedirecter implements IAuthenticationClientRedirecter {
    static inject = () => [EnvConfiguration, Device, Logger];
    constructor(private config: EnvConfiguration, private device: Device, private log: Logger){}

    private redirectWithIOSASWebAuthenticationSession = async ({ urlWithoutQueryParams, queryParams }: { urlWithoutQueryParams: string, queryParams: URLSearchParams }): Promise<boolean> => {
        try {
            const appScheme = 'io.autopylot.app';

            const redirectURIHash = queryParams.get('redirect_uri')!.split('#')[1]!;

            queryParams.set('redirect_uri', `${appScheme}://#${redirectURIHash}`);

            const urlWithAppSchemeRedirect = `${urlWithoutQueryParams}?${queryParams.toString()}`;

            this.log.info('ASWebAuthSession.start', { urlWithAppSchemeRedirect });

            const callbackUrl: string = await new Promise((resolve, reject) => (window as any).plugins.ASWebAuthSession.start(appScheme, urlWithAppSchemeRedirect, resolve, reject));
            const hash = decodeURI(callbackUrl.split('io.autopylot.app://')[1]);

            this.log.info('ASWebAuthSession response', { callbackUrl, hash });

            // flush logs before reloading app so we don't lose anything
            await this.log.flushFileSystemLogs();

            // in the unexpected case that the url is not the expected format,
            // ignore it and let the reload take care of the auth state
            if (hash) {
                window.location.hash = hash;
            }

            // warning: make sure there is no async code between setting hash and reloading
            // (otherwise the hash change will be detected by the router and discarded)
            window.location.reload();

            // we could return true here to indicate success to the caller,
            // but we are reloading the app, so best to never resolve
            await new Promise(resolve  => {});

            // never get here, but need to make ts happy
            return true;

        } catch (e: any) {
            // unfortunately, e is always "error" so we can't determine what went wrong,
            // most likely auth flow was aborted by the user, so just do nothing
            return false;
        }
    };

    private waitingForAuthCallback = false;
    private appUrlListenerPromise?: Promise<PluginListenerHandle>;

    redirectWithInAppBrowser = async ({ urlWithoutQueryParams, queryParams }: { urlWithoutQueryParams: string, queryParams: URLSearchParams }) => {
        // the app will see this host, close the in app browser and trigger 'appUrlOpen'
        // (this also needs to be recognized by the api as a valid callback)
        const redirectBaseUrl = (this.config.ENV === 'live' ? 'https://app.autopylot.io' : 'https://app.staging.autopylot.io');

        const redirectURIHash = queryParams.get('redirect_uri')!.split('#')[1]!;

        queryParams.set('redirect_uri', `${redirectBaseUrl}/#${redirectURIHash}`);

        const urlWithRedirect = `${urlWithoutQueryParams}?${queryParams.toString()}`;

        return await new Promise<boolean>(resolve => {

            if (!this.appUrlListenerPromise) {
                // only setting up one listener once
                // we used to set this up for each time we start auth, and then dispose of it when they closed the browser (eg on dismiss)
                // this worked for most users, but some users had a 16s delay... so we don't know when its safe to stop listening
                // so the new solution is to never stop listening
                this.appUrlListenerPromise = App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
                    // example url: https://app.autopylot.io/#/missions, path = #/missions
                    const path = event.url.split('.autopylot.io/').pop();

                    this.log.info('appUrlOpen', { eventUrl: event.url, path });

                    const isOnAuthCallbackUrl = path?.includes('#/oauth/code');

                    if (!this.waitingForAuthCallback && isOnAuthCallbackUrl){
                        this.log.info('appUrlOpen -- on auth callback url but was not expecting one, allowing to proceed');
                        // this probably "shouldn't happen" and from a security perspective we should block it
                        // but since our focus right now is unblocking our users that are getting stuck with no way to login
                        // we're going to allow it to proceed
                    }
                    else if (this.waitingForAuthCallback && !isOnAuthCallbackUrl){
                        this.log.info('appUrlOpen -- not #/oauth/code path, ignoring');
                    }

                    // we're about to reload so this doesn't matter, but for completeness doing it anyway
                    this.waitingForAuthCallback = false;

                    // flush logs before reloading app so we don't lose anything
                    await this.log.flushFileSystemLogs();

                    // in the unexpected case that the url is not the expected format,
                    // ignore it and let the reload take care of the auth state
                    if (path) {
                        window.location.hash = path;
                    }

                    // warning: make sure there is no async code between setting hash and reloading
                    // (otherwise the hash change will be detected by the router and discarded)
                    window.location.reload();
                });
            }


            const browserListenerPromise = Browser.addListener('browserFinished', async () => {
                this.log.info('BrowserAuthenticationClientRedirecter - browserFinished');
                // remove the browser listener,we're done with it
                // we could remove all as we expect only one, but not worth the FUD (what if a 2nd one is in flight, that we shouldn't be touching)
                browserListenerPromise.then(listener => listener.remove());

                if (this.waitingForAuthCallback) {
                    resolve(false);
                }
                // otherwise, we never resolve, our listener will reload the app

                // if they cancelled, then we shouldn't be "waiting anymore"
                // but this area of code has a history of struggling to distinguish cancel
                // at this time, our focus is trying to get this working for users where failure means
                // they have no way to login and so setting this.waitingForAuthCallback = false on
                // cancel isn't a priority
            });

            this.log.info('Browser.open', { urlWithRedirect });

            this.waitingForAuthCallback = true;
            Browser.open({ url: urlWithRedirect, windowName: '_self' });
        });
    };

    redirect = async (url: string): Promise<boolean> => {
        this.log.info('BrowserAuthenticationClientRedirecter.redirect', { url });

        const [ urlWithoutQueryParams, queryParamsString] = url.split('?');
        const queryParams = new URLSearchParams(queryParamsString);

        const isRedirectingToAuth0ForLogin = url.includes('autopylot.io/v1/oauth/authorize');

        if (this.device.platform === 'ios' && isRedirectingToAuth0ForLogin){
            return await this.redirectWithIOSASWebAuthenticationSession({ urlWithoutQueryParams, queryParams });
        }
        else if (this.device.platform === 'android' && isRedirectingToAuth0ForLogin){
            return await this.redirectWithInAppBrowser({ urlWithoutQueryParams, queryParams });
        }
        else {
            window.location.href = url;
            // block indefinitely (so that further code can't run while we're trying to redirect)
            return await new Promise(resolve  => {});
        }
    };
}
